import { useApolloClient, useMutation } from "@apollo/client";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import React, {
  FC,
  ChangeEvent,
  useState,
  useCallback,
  SetStateAction,
} from "react";
import {
  WrappedComponentProps,
  injectIntl,
  FormattedMessage,
} from "react-intl";
import { MESSAGE_TYPE, SAPHEER_COLOR } from "../../../utils/constant";
import { displaySnackBar } from "../../../utils/snackBarUtils";
import Address from "../Address/Address";
import Person from "../Person/Person";
import { CircularProgress, Typography } from "@material-ui/core";
import { isEmailValid } from "../../../utils/Validator";
import ErrorIcon from "@material-ui/icons/Error";
import {
  CheckEmail,
  CheckEmailVariables,
} from "../../../graphql/onboarding/member/types/CheckEmail";
import { CHECK_EMAIL } from "../../../graphql/onboarding/member/mutation";
import { SapheerError } from "../../../enums/error";
import { SelectItemInterface } from "../../../interface/SelectItemInterface";
import { FileInterfaceLinkcy } from "../../../interface/FileInterface";
import {
  CreateMemberInput,
  CreateUserInput,
} from "../../../types/graphql-global-types-onboarding";
import { DocumentType } from "../../../enums/document";
import CustomTextField from "../../Ui/CustomTextField";
import { formatPhoneNumberIntl } from "../../../utils/phone";
import { Mail, Phone } from "lucide-react";
import CustomCheckBox from "../../Ui/CustomCheckBox";
import CustomPhone from "../../Ui/CustomPhoneField";
import DocumentUser from "../DocumentUser/DocumentUser";
import { DocumentsType_documentsType } from "../../../graphql/onboarding/document/types/DocumentsType";

const styles = makeStyles((theme) => ({
  PersonFormMultiple: {
    maxWidth: 450,
    paddingBottom: theme.spacing(4),
  },
  doubleInput: {
    display: "flex",
    "& > *": {
      width: "calc(50% - 8px)",
      maxWidth: 450,
    },
    "& > *:first-child": {
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
      display: "block",
      "& > *": {
        width: "100%",
        maxWidth: 450,
      },
      "& > *:first-child": {
        marginRight: 0,
      },
    },
  },
  emailLoading: {
    display: "flex",
    alignItems: "center",
  },
  checkbox: {
    "& label>span:first-of-type": {},
  },
  text: {
    fontSize: ".75rem",
    lineHeight: 1.5,
    whiteSpace: "normal",
    overflowWrap: "break-word",
  },
}));

interface PersonFormMultiplePropsInterface {
  isShareholder?: boolean;
  isAgree: boolean;
  isAccept: boolean;
  information: CreateUserInput;
  setInformation(info: SetStateAction<CreateUserInput>): void;
  email: string;
  phone: string;
  personId?: number | null;
  isSubmitted?: boolean;
  identitySelect: SelectItemInterface[];
  handleIsAgreeChange(
    personId: number,
  ): (event: ChangeEvent<HTMLInputElement>) => void;
  handleSelect(personId: number): (name: string, value: string) => void;
  handlePhoneChange(personId: number, phone: string): void;
  handleEmailChange(
    personId: number,
  ): (event: ChangeEvent<HTMLInputElement>) => void;
  handleInputChange(
    personId: number,
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateChange(personId: number): (name: string, date: any) => void;
  handleAcceptChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleUpload: (
    type: string,
    files: FileInterfaceLinkcy[],
    docType?: string,
  ) => void;
  handleRemoveFile: (type: string) => void;
  documentsType: (DocumentsType_documentsType | null)[];
}

const PersonFormMultiple: FC<
  WrappedComponentProps & PersonFormMultiplePropsInterface
> = (props) => {
  const classes = styles();
  const {
    intl,
    information,
    handleSelect: _handleSelect,
    handleInputChange,
    handlePhoneChange: _handlePhoneChange,
    handleDateChange,
    isAgree,
    isAccept,
    handleAcceptChange,
    isSubmitted,
    handleUpload: _handleUpload,
    handleRemoveFile: _handleRemoveFile,
    setInformation,
    documentsType,
  } = props;
  const personId = props.personId ? props.personId : 0;
  const client = useApolloClient();
  const identityDoc = information?.documents?.find(
    (doc) =>
      doc?.type === DocumentType.PASSPORT ||
      doc?.type == DocumentType.DRIVING_LICENCE ||
      doc?.type == DocumentType.IDENTITY_CARD ||
      doc?.type == DocumentType.VISA ||
      doc?.type == DocumentType.RESIDENCE_PERMIT,
  );

  const [files, setFiles] = useState<FileInterfaceLinkcy[]>([]);
  const [docType, setDocType] = useState<string | undefined>(
    identityDoc?.type ?? undefined,
  );
  const [isEmailNotValid, setIsEmailNotValid] = useState(false);
  const [checkEmail, { loading: checkEmailsLoading }] = useMutation<
    CheckEmail,
    CheckEmailVariables
  >(CHECK_EMAIL, {
    onCompleted: () => {
      setIsEmailNotValid(false);
    },
    onError: (error) => {
      const { message } = error?.graphQLErrors?.[0] || {};
      if (message === SapheerError.EMAIL_ALREADY_EXIST) {
        setIsEmailNotValid(true);
        displaySnackBar(client, {
          isOpen: true,
          type: MESSAGE_TYPE.ERROR,
          message: intl.formatMessage({ id: "member.email.already.exists" }),
        });
      } else {
        setIsEmailNotValid(false);
      }
    },
  });

  const handlePhoneChange = (name: string, value: string) => {
    const event = {
      target: {
        name,
        value,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    handleInputChange(personId)(event);
  };

  const _checkEmail = (_email: string) => {
    if (_email && isEmailValid(_email)) {
      checkEmail({
        variables: {
          email: _email.toLowerCase().trim(),
          id: information?.id || "",
        },
      });
    }
  };

  const upload = useCallback(
    (_files: FileInterfaceLinkcy[], _docType?: string) => {
      if (_files.length && _docType) {
        _handleUpload("IDENTITY_DOC", _files, _docType);
      }
    },
    [files, docType, _handleUpload],
  );

  const handleSelect = useCallback(
    (index: number) => (name: string, value: string) => {
      if (name == "identidyDoc") {
        setDocType(value);
        upload(files, value);
        return;
      } else {
        _handleSelect(index)(name, value);
      }
    },
    [docType, files, _handleSelect],
  );

  return (
    <Box className={classes.doubleInput}>
      <Box>
        <Box className={classes.emailLoading}>
          <CustomTextField
            name="email"
            label={<FormattedMessage id="form.email" />}
            placeholder="exemple@email.com"
            onChange={handleInputChange(personId)}
            errorMessageId="account.email.empty"
            value={information?.email || ""}
            iconLeft={Mail}
            isSubmitted={isSubmitted}
          />

          {checkEmailsLoading && (
            <CircularProgress size={30} style={{ marginLeft: 8 }} />
          )}
          {information?.email && !isEmailValid(information.email) ? (
            <ErrorIcon style={{ marginLeft: 8, color: "#FFCC00" }} />
          ) : isEmailNotValid ? (
            <ErrorIcon style={{ marginLeft: 8, color: "#FFCC00" }} />
          ) : (
            ""
          )}
        </Box>
        <CustomPhone
          name="mobilePhoneNumber"
          label="Téléphone"
          onChange={handlePhoneChange}
          errorMessageId="phone.empty"
          value={formatPhoneNumberIntl(information?.mobilePhoneNumber || "")}
          isSubmitted={isSubmitted}
        />
        <Person
          information={information}
          handleInputChange={handleInputChange(personId)}
          handleSelect={handleSelect(personId)}
          handleDateChange={(name, value) =>
            handleDateChange(personId)(name, value)
          }
          isSubmitted={isSubmitted}
        />
      </Box>
      <Box>
        <Address
          information={information}
          handleInputChange={handleInputChange(personId)}
          isSubmitted={isSubmitted}
          handleSelect={handleSelect(personId)}
        />
        <DocumentUser
          information={information}
          setInformation={setInformation}
          documentsType={documentsType ?? []}
        />
        <CustomCheckBox
          allPointer
          name="isAgree"
          isSubmitted={!!isSubmitted}
          isChecked={isAgree}
          onCheck={handleAcceptChange}
          label={
            <Typography
              className={classes.text}
              style={{ fontWeight: "bold", color: SAPHEER_COLOR }}
            >
              <FormattedMessage id="member.address.agree" />
            </Typography>
          }
          errorMessage={<FormattedMessage id="stepFive.form.error.agree" />}
        />
        <CustomCheckBox
          allPointer
          name="accept"
          isSubmitted={!!isSubmitted}
          isChecked={isAccept}
          onCheck={handleAcceptChange}
          label={
            <Typography
              className={classes.text}
              style={{ fontWeight: "bold", color: SAPHEER_COLOR }}
            >
              <FormattedMessage id="member.certify.information" />
            </Typography>
          }
          errorMessage={<FormattedMessage id="stepFive.createPerson.accept" />}
        />
      </Box>
    </Box>
  );
};

export default injectIntl(PersonFormMultiple);
